import React, { useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaArrowRight, FaBox, FaTag, FaStore } from 'react-icons/fa';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import { fetchAdminProductDetails, clearError, clearProductDetails } from '../../../redux/Slices/AdminProductsSlice';

const AdminProductDetails = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { productId } = useParams();
    
    const { productDetails, detailsLoading, error } = useSelector((state) => state.adminProducts);

    useEffect(() => {
        setTitle('تفاصيل المنتج');
        dispatch(fetchAdminProductDetails(productId));
        
        return () => {
            dispatch(clearProductDetails());
        };
    }, [setTitle, dispatch, productId]);

    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    if (detailsLoading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل تفاصيل المنتج...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!productDetails) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <p className="text-muted">لا توجد بيانات</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">تفاصيل المنتج</h4>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn text-white"
                        onClick={() => navigate('/admin-products')}
                        style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                        <FaArrowRight className="mx-2" />
                        العودة للقائمة
                    </motion.button>
                </div>

                {/* Product Info Card */}
                <div className="card shadow-lg border-0 mb-4">
                    <div className="card-body p-4">
                        <div className="row">
                            {/* Images */}
                            {productDetails.images && productDetails.images.length > 0 && (
                                <div className="col-md-12 mb-4">
                                    <h6 className="text-muted mb-3">صور المنتج</h6>
                                    <div className="row">
                                        {productDetails.images.map((image, index) => (
                                            <div key={index} className="col-md-3 mb-3">
                                                <img
                                                    src={image}
                                                    alt={`Product ${index + 1}`}
                                                    className="w-100 rounded"
                                                    style={{ height: '200px', objectFit: 'cover' }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Basic Info */}
                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaBox className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">اسم المنتج</h6>
                                </div>
                                <p className="fw-bold">{productDetails.name || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaTag className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">السعر الافتراضي</h6>
                                </div>
                                <p className="fw-bold">
                                    {(() => {
                                        const price = 
                                            productDetails?.default_price ??
                                            productDetails?.price ??
                                            productDetails?.final_price ??
                                            productDetails?.min_price ??
                                            productDetails?.max_price;
                                        return (price || price === 0)
                                            ? `${price} ج.م`
                                            : 'غير محدد';
                                    })()}
                                </p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <h6 className="text-muted mb-2">الخصم</h6>
                                <p className="fw-bold">{productDetails.discount ? `${productDetails.discount}%` : 'لا يوجد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <h6 className="text-muted mb-2">الحالة</h6>
                                {productDetails.status ? (
                                    <span className="badge bg-success">نشط</span>
                                ) : (
                                    <span className="badge bg-danger">غير نشط</span>
                                )}
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaStore className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">المتجر</h6>
                                </div>
                                <p className="fw-bold">{productDetails.store_name || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <h6 className="text-muted mb-2">الفرع</h6>
                                <p className="fw-bold">{productDetails.branch_name || 'الفرع الرئيسي'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <h6 className="text-muted mb-2">الفئة</h6>
                                <p className="fw-bold">{productDetails.category_name || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <h6 className="text-muted mb-2">الفئة الفرعية</h6>
                                <p className="fw-bold">{productDetails.subcategory_name || 'غير محدد'}</p>
                            </div>

                            {productDetails.description && (
                                <div className="col-md-12 mb-3">
                                    <h6 className="text-muted mb-2">الوصف</h6>
                                    <p className="fw-bold">{productDetails.description}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Variants */}
                {productDetails.variants && productDetails.variants.length > 0 && (
                    <div className="card shadow-lg border-0">
                        <div className="card-header" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}>
                            <h5 className="mb-0">الاختيارات والأسعار</h5>
                        </div>
                        <div className="card-body p-4">
                            {productDetails.variants.map((variant, index) => (
                                <div key={variant.id} className="mb-4 pb-4 border-bottom">
                                    <h6 className="text-primary mb-3">
                                        الاختيار {index + 1}: {variant.unit_name} ({variant.unit_type})
                                    </h6>
                                    <p className="mb-2"><strong>السعر:</strong> {variant.price} ج.م</p>
                                    
                                    {variant.colors && variant.colors.length > 0 && (
                                        <div className="mt-3">
                                            <h6 className="text-muted mb-2">الألوان المتاحة:</h6>
                                            <div className="table-responsive">
                                                <table className="table table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>اللون</th>
                                                            <th>السعر</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {variant.colors.map((color) => (
                                                            <tr key={color.id}>
                                                                <td>{color.name || `لون ${color.id}`}</td>
                                                                <td>{color.price} ج.م</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminProductDetails;

